import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin } from 'lucide-react';
import { Restaurant } from '../../lib/types';
import { isRestaurantOpenBySchedule } from '../../lib/schedule';
interface RestaurantCardProps {
  restaurant: Restaurant;
  zoneName?: string;
  tags?: string[];
}
export function RestaurantCard({
  restaurant,
  zoneName,
  tags = []
}: RestaurantCardProps) {
  const scheduleStatus = isRestaurantOpenBySchedule(restaurant.slug);
  const isActuallyOpen = scheduleStatus.hasSchedule ? scheduleStatus.isOpen : false;
  const isCapricho = restaurant.slug === 'capricho-de-canela' || String(restaurant.id) === '2';
  const isKabu = restaurant.slug === 'kabu';
  const isEmilius = restaurant.slug === 'emilius-pizza';
  const isCongelados = restaurant.slug === 'heladeria-congelados';
  const primary = isCapricho ? '#8B5E3C' : isKabu ? '#D4A853' : isEmilius ? '#D42B2B' : isCongelados ? '#7B2D8E' : restaurant.primary_color || '#C9A34E';
  const secondary = restaurant.secondary_color || '#FFFFFF';
  const accent = isCapricho ? '#C8A96A' : isKabu ? '#E8751A' : isEmilius ? '#D42B2B' : isCongelados ? '#F472B6' : restaurant.accent_color || primary;
  const isCrackPollo = restaurant.slug === 'crack-pollo' || String(restaurant.id) === '20';
  const isCabanaParrillera = restaurant.slug === 'la-cabana-parrillera' || String(restaurant.id) === '24';
  const isThursdayUTC = new Date().getDay() === 4;
  const coverUrl = isCapricho ? 'https://cdn.magicpatterns.com/uploads/1PV5hU3phUPpmdu51L1wAD/27E9AE19-B896-4B56-82B1-CEE31AD5C925.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isCongelados ? 'https://cdn.magicpatterns.com/uploads/24WhrxVDroFFjBpfomMhk3/ChatGPT_Image_26_abr_2026,_23_23_00.png' : isCrackPollo ? 'https://cdn.magicpatterns.com/uploads/vkpirHwDmy1iyMJzGb5usA/ChatGPT_Image_30_abr_2026,_00_44_20.png' : restaurant.cover_url || 'https://cdn.magicpatterns.com/uploads/9aYwqfVJGQEzKkCYeBBzcT/MX.jpg';
  const logoUrl = isCapricho ? 'https://cdn.magicpatterns.com/uploads/qfQUG6xSwCibNddEpouoD3/ChatGPT_Image_21_mar_2026,_21_58_30.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isCongelados ? 'https://cdn.magicpatterns.com/uploads/24WhrxVDroFFjBpfomMhk3/ChatGPT_Image_26_abr_2026,_23_23_00.png' : isCrackPollo ? 'https://cdn.magicpatterns.com/uploads/vrdnrQseiRNenTxSZL7mGp/ChatGPT_Image_30_abr_2026,_00_42_25.png' : restaurant.logo_url;
  return <Link to={`/r/${restaurant.slug}`} className="block group" data-id="element-2170" data-mp="tthu3jee15bg">
      <div className="relative h-[300px] rounded-2xl overflow-hidden border border-white/5 bg-[#141414] transition-shadow duration-300 hover:shadow-[0_0_24px_rgba(201,163,78,0.12)]" data-id="element-2171" data-mp="tsyl4s6ptr9k">
        {/* Cover Image — uses cover_url, or a vibrant fallback */}
        <img src={coverUrl} alt={restaurant.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" data-id="element-2172" data-mp="ftgx4f18ui19" />

        {/* Dark gradient overlay — stronger at bottom for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" data-id="element-2173" data-mp="ks0iad80jpsk" />

        {/* Colored accent glow at bottom — uses restaurant's primary color */}
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20 pointer-events-none" style={{
        background: `linear-gradient(to top, ${primary}40, transparent)`
      }} data-id="element-2174" data-mp="1w8qf55qjr59i" />

        {/* Top badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between z-10" data-id="element-2175" data-mp="2vljxo0305ukq">
          <div className="flex flex-col gap-1.5" data-id="element-2176" data-mp="30kwk4jpyozms">
            {restaurant.is_featured && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{
            backgroundColor: primary,
            color: '#0B0B0B'
          }} data-id="element-2177" data-mp="35ftp9fk4eoue">
                ★ Destacado
              </span>}
            {isCapricho && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-white shadow-lg" data-id="element-2178" data-mp="3o7wcxav6j5e5">
                🎉 2do al 50%
              </span>}
            {/* Cabaña Parrillera — Promo Jueves DESACTIVADA por pedido del
             dueño. El badge ya no se muestra en el marketplace. */}
            {/* Congelados promo badge removed — restaurant runs at normal prices. */}
          </div>

          {!isActuallyOpen ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/90 text-white" data-id="element-2179" data-mp="1cn58q299o2un">
              Cerrado
            </span> : <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60" data-id="element-2180" data-mp="20an385jx1f1b">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-2181" data-mp="20czkshlrhndu" />
              <span className="text-[10px] font-medium text-white/80" data-id="element-2182" data-mp="1vi3r7d0vmrym">
                Abierto
              </span>
            </span>}
        </div>

        {/* Bottom content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10" data-id="element-2183" data-mp="2o1sj2kpuw7bm">
          <div className="flex items-end gap-3" data-id="element-2184" data-mp="2j994bkffn658">
            {/* Logo */}
            <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border-2" style={{
            borderColor: `${primary}60`,
            backgroundColor: isCongelados ? '#FFFFFF' : '#0B0B0B'
          }} data-id="element-2185" data-mp="hbz1jlu5bcnh">
              {logoUrl ? <img src={logoUrl} alt={`${restaurant.name} logo`} className={`w-full h-full ${isCapricho ? 'object-contain bg-[#FBF7F2] p-0.5' : isKabu ? 'object-contain bg-[#E0B87B] p-0.5' : isEmilius ? 'object-contain bg-[#F2E6D5] p-0.5' : isCongelados ? 'object-contain bg-white p-0.5' : isCrackPollo ? 'object-contain bg-white p-0.5' : 'object-cover'}`} loading="lazy" decoding="async" data-id="element-2186" data-mp="cl8qbrz1gfkv" /> : <div className="w-full h-full flex items-center justify-center font-bold text-lg" style={{
              backgroundColor: primary,
              color: secondary
            }} data-id="element-2187" data-mp="1j0sv8ovqkbtc">
                  {restaurant.name.charAt(0)}
                </div>}
            </div>

            {/* Text content — name always white for readability */}
            <div className="flex-1 min-w-0" data-id="element-2188" data-mp="3i9sspspznx34">
              <h3 className="font-bold text-lg leading-tight line-clamp-1 mb-0.5 text-white" data-id="element-2189" data-mp="3dht1conzici5">
                {restaurant.name}
              </h3>
              <p className="text-white/60 text-xs line-clamp-1" data-id="element-2190" data-mp="3r7cxhbn28bih">
                {restaurant.description_short || 'Deliciosa comida preparada con amor.'}
              </p>
            </div>
          </div>

          {/* Meta row */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10" data-id="element-2191" data-mp="2wcogidmeow">
            <div className="flex items-center gap-3" data-id="element-2192" data-mp="26st3x72uo0du">
              <div className="flex items-center gap-1 text-white/50" data-id="element-2193" data-mp="26tmc8chgixy3">
                <MapPin className="w-3 h-3" data-id="element-2194" data-mp="21uriifhyboz7" />
                <span className="text-[11px] font-medium" data-id="element-2195" data-mp="21w2qysmcdkwn">
                  {zoneName || restaurant.address_text || 'Los Santos'}
                </span>
              </div>
              <div className="flex items-center gap-1" data-id="element-2196" data-mp="1kym1u98s4nnp">
                <Star className="w-3 h-3" style={{
                color: primary,
                fill: primary
              }} data-id="element-2197" data-mp="1pqvs5qdt3pqi" />
                <span className="text-[11px] font-semibold" style={{
                color: primary
              }} data-id="element-2198" data-mp="x280nd2gs44i">
                  4.8
                </span>
              </div>
            </div>

            <span className="text-xs font-bold transition-colors" style={{
            color: accent
          }} data-id="element-2199" data-mp="16yuma42m6z7l">
              {isActuallyOpen ? 'Ordenar →' : 'Ver menú →'}
            </span>
          </div>
        </div>
      </div>
    </Link>;
}